import clsx from 'clsx'
import React from 'react'
import { type MessageDescriptor, useIntl } from 'react-intl'
import styled from 'styled-components'
import {
  middleDotSeparatorMessage,
  pipeSeparatorMessage,
  bulletSeparatorMessage,
  dashSeparatorMessage,
} from 'LuxKit/intl/separators'

const SeparatorSpan = styled.span`
  &.start-space {
    margin-inline-start: 0.25em;
  }

  &.end-space {
    margin-inline-end: 0.25em;
  }
`

const separatorMessages = {
  middot: middleDotSeparatorMessage,
  pipe: pipeSeparatorMessage,
  bullet: bulletSeparatorMessage,
  dash: dashSeparatorMessage,
} as const

type SeparatorVariant = keyof typeof separatorMessages

interface Props {
  /** A built-in variant name, or a custom MessageDescriptor */
  variant: SeparatorVariant | MessageDescriptor
  /** Render a space before the separator character */
  startSpace?: boolean
  /** Render a space after the separator character */
  endSpace?: boolean
}

/**
 * Renders a decorative inline separator character (e.g. `·`, `|`, `•`).
 *
 * Marked `aria-hidden` since the separator is purely visual.
 * Spacing can be optionally included as `startSpace` and `endSpace` props.
 *
 * To add a new built-in variant:
 * 1. Define a message in `LuxKit/intl/separators.ts` using `defineMessage()`
 * 2. Add it to the `separatorMessages` map above
 *
 * For one-off separators, pass a `MessageDescriptor` directly as `variant`.
 *
 * @example
 * ```tsx
 * <span>Sydney</span>
 * <Separator variant="middot" />
 * <span>3 nights</span>
 * ```
 */
function Separator({ variant, startSpace, endSpace }: Props) {
  const intl = useIntl()
  const message = typeof variant === 'string'
    ? separatorMessages[variant]
    : variant
  return <SeparatorSpan
    aria-hidden="true"
    className={clsx({ 'start-space': startSpace, 'end-space': endSpace })}
  >
    {intl.formatMessage(message)}
  </SeparatorSpan>
}

export default Separator
